import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Eye, EyeOff, Palmtree } from 'lucide-react';
import WaveButton from '@/components/WaveButton';
import VersionInfo from '@/components/VersionInfo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Login({ onLogin }) {
  const navigate = useNavigate();
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      toast.success('¡Bienvenido!');
      onLogin(response.data.user, response.data.access_token);
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-8">
      <div className="w-full max-w-md space-y-8">
        {/* Logo */}
        <div className="flex justify-center">
          <img src="/logo.png" alt="Ciro del Pacífico" className="h-32 w-auto" />
        </div>

        {/* Formulario */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <form onSubmit={handleLogin} className="space-y-6">
            {/* Campo Usuario */}
            <div className="space-y-2">
              <Input
                type="text"
                placeholder="Usuario"
                value={loginData.username}
                onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                required
                className="h-12 rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500"
              />
            </div>

            {/* Campo Contraseña */}
            <div className="space-y-2 relative">
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Contraseña"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                required
                className="h-12 rounded-xl border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {/* Botón Iniciar Sesión con efecto Wave */}
            <WaveButton
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-base shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </WaveButton>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500 space-y-4">
          <div>
            <p>Sistema de Gestión Agrícola y Ganadero</p>
            <p className="text-xs mt-1">© 2025 Todos los derechos reservados</p>
            <p className="text-xs mt-1">Creado por balpizaar</p>
          </div>
          
          {/* Módulos Principales */}
          <div className="grid grid-cols-2 gap-4 pt-4">
            <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-100 flex items-center justify-center">
              <div className="text-center">
                <Palmtree className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                <p className="text-sm font-semibold text-emerald-700">Palmicultura</p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-xl p-4 border border-emerald-100 flex items-center justify-center">
              <div className="text-center">
                <svg className="w-8 h-8 text-emerald-600 mx-auto mb-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 7c-1.5 0-3 1-3 3v4c0 2 1.5 3 3 3" />
                  <path d="M5 7c1.5 0 3 1 3 3v4c0 2-1.5 3-3 3" />
                  <path d="M12 7v10" />
                  <circle cx="12" cy="5" r="2" />
                  <circle cx="12" cy="19" r="2" />
                </svg>
                <p className="text-sm font-semibold text-emerald-700">Ganadería</p>
              </div>
            </div>
          </div>
          
          {/* Información de Versión */}
          <VersionInfo />
        </div>
      </div>
    </div>
  );
}
